import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { useShallow } from 'zustand/react/shallow';

import type { CartItem } from '@/types';

interface CartState {
  items: CartItem[];
  add: (item: Omit<CartItem, 'qty'> & { qty?: number }) => void;
  setQty: (key: string, qty: number) => void;
  remove: (key: string) => void;
  clear: () => void;
}

export const useCartStore = create<CartState>()(
  persist(
    (set) => ({
      items: [],
      add: (item) =>
        set((state) => {
          const qty = item.qty ?? 1;
          const i = state.items.findIndex((x) => x.key === item.key);
          if (i >= 0) {
            const next = state.items.slice();
            const existing = next[i];
            if (existing) next[i] = { ...existing, qty: existing.qty + qty };
            return { items: next };
          }
          return { items: [...state.items, { ...item, qty }] };
        }),
      setQty: (key, qty) =>
        set((state) => ({
          items: state.items
            .map((x) => (x.key === key ? { ...x, qty: Math.max(0, qty) } : x))
            .filter((x) => x.qty > 0),
        })),
      remove: (key) => set((state) => ({ items: state.items.filter((x) => x.key !== key) })),
      clear: () => set({ items: [] }),
    }),
    {
      name: 'moct.cart',
      storage: createJSONStorage(() => localStorage),
      version: 1,
    },
  ),
);

export function useCart() {
  return useCartStore(
    useShallow((s) => ({
      items: s.items,
      add: s.add,
      setQty: s.setQty,
      remove: s.remove,
      clear: s.clear,
    })),
  );
}

export const useCartCount = (): number =>
  useCartStore((s) => s.items.reduce((n, x) => n + x.qty, 0));

export const useCartSubtotal = (): number =>
  useCartStore((s) => s.items.reduce((n, x) => n + x.qty * x.price, 0));
